// BS-6442. Clasifica lo que cambió al guardar una carta para decidir qué sync pedir.
//
// Si los ÚNICOS cambios son de disponibilidad, se informa solo eso (SQS liviano -> turn_off /
// turn_on por SKU en la plataforma) en lugar de reenviar el menú completo. Rappi descarta menús
// encolados de la misma tienda, así que reenviar todo hace que los cambios tarden horas en
// reflejarse.
//
// Criterio WHITELIST a propósito: ante la mínima duda devolvemos 'full'. Un 'full' de más solo
// cuesta un menú completo (lo de hoy); un 'availability-only' de más perdería el otro cambio.
//
// Prender también entra (revisado el 6-ago-2026): el apagado por SKU no toca el menú publicado,
// así que el ítem sigue existiendo del lado de la plataforma y se puede volver a prender. Si una
// republicación intermedia lo sacó de la carta, el SyncMenuWorker lo detecta y cae al menú
// completo por su cuenta.

const isAvailable = (product) =>
  product?.isProductAvailable === null || product?.isProductAvailable === undefined
    ? true
    : product.isProductAvailable === true;

// Índice sku -> { categoryName, price, currentPrice, available }
function indexProducts(productsByMenu) {
  const index = new Map();
  for (const category of productsByMenu || []) {
    for (const product of category?.products || []) {
      if (!product?.sku) continue;
      index.set(String(product.sku), {
        categoryName: category?.name ?? null,
        price: String(product.price ?? ''),
        currentPrice: String(product.currentPrice ?? ''),
        available: isAvailable(product),
      });
    }
  }
  return index;
}

function sameOrigins(originsBefore, checkedOriginsAfter) {
  const before = new Set((originsBefore || []).map((o) => o?.name).filter(Boolean));
  const after = new Set((checkedOriginsAfter || []).filter(Boolean));
  if (before.size !== after.size) return false;
  for (const name of before) {
    if (!after.has(name)) return false;
  }
  return true;
}

const FULL = { kind: 'full' };

export function classifyMenuChanges({
  menuBefore,
  productsBefore,
  menuAfter,
  productsAfter,
  checkedOriginsAfter,
}) {
  // Sin snapshot no hay con qué comparar.
  if (!menuBefore || !productsBefore || !menuAfter || !productsAfter) return FULL;

  if ((menuBefore.name ?? '') !== (menuAfter.name ?? '')) return FULL;
  if (!sameOrigins(menuBefore.origins, checkedOriginsAfter)) return FULL;

  const before = indexProducts(productsBefore);
  const after = indexProducts(productsAfter);

  if (before.size !== after.size) return FULL;

  const turnOff = [];
  const turnOn = [];
  for (const [sku, afterProduct] of after) {
    const beforeProduct = before.get(sku);
    if (!beforeProduct) return FULL; // SKU nuevo
    if (beforeProduct.categoryName !== afterProduct.categoryName) return FULL;
    if (beforeProduct.price !== afterProduct.price) return FULL;
    if (beforeProduct.currentPrice !== afterProduct.currentPrice) return FULL;

    if (beforeProduct.available === afterProduct.available) continue;
    if (afterProduct.available === true) turnOn.push(sku);
    else turnOff.push(sku);
  }

  if (turnOff.length === 0 && turnOn.length === 0) return FULL;

  return { kind: 'availability-only', turnOff, turnOn };
}

// BS-6442. Body del POST /api/productsV2/syncProductsWithFds. El campo availabilityOnly se OMITE
// si no hay SKUs que informar: así el request queda idéntico al de siempre en el camino histórico.
// Cada lista viaja solo si tiene elementos, para no mandar arrays vacíos al backend.
export function buildSyncProductsWithFdsPayload(menuOrigins, shopCode, availabilityOnly) {
  const payload = { origins: menuOrigins ?? null, shopCode };

  const turnOff = availabilityOnly?.turnOff ?? [];
  const turnOn = availabilityOnly?.turnOn ?? [];

  if (turnOff.length > 0 || turnOn.length > 0) {
    payload.availabilityOnly = {};
    if (turnOff.length > 0) payload.availabilityOnly.turnOff = turnOff;
    if (turnOn.length > 0) payload.availabilityOnly.turnOn = turnOn;
  }

  return payload;
}

export default { classifyMenuChanges, buildSyncProductsWithFdsPayload };
